import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnInit
} from '@angular/core';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { concat, concatMap, defer, of, takeWhile } from 'rxjs';
import { ContactForm } from 'src/app/classes/form-groups/contact-form';
import { Contact } from 'src/app/classes/models';
import { SubNationalDivision } from 'src/app/classes/models/sub-national-division';
import { ContactService } from 'src/app/services/contact.service';
import { SubNationalDivisionService } from 'src/app/services/sub-national-division.service';
import { ToastService } from 'src/app/services/toast.service';
import { UtilityService } from 'src/app/services/utility.service';

import { ConfirmModalComponent } from '../confirm-modal/confirm-modal.component';

@Component({
  selector: 'app-add-edit-contact-modal',
  templateUrl: './add-edit-contact-modal.component.html',
  styleUrls: ['./add-edit-contact-modal.component.less'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class AddEditContactModalComponent implements OnInit {
  @Input() SiteId?: number;
  @Input() isSiteContact?: boolean;
  @Input() useStreetAddress: boolean = false;
  @Input() useMailingAddress: boolean = false;
  @Input() ContactId?: number;

  SubNationalDivisions!: SubNationalDivision[];
  contactForm?: ContactForm;
  Contact?: Contact;

  previousPrimaryContact?: Contact;

  constructor(
    private SubNationalDivisionService: SubNationalDivisionService,
    private ToastService: ToastService,
    private ContactService: ContactService,
    private UtilityService: UtilityService,
    private ModalService: NgbModal,
    private modal: NgbActiveModal
  ) {
    this.contactForm = new ContactForm();

    this.SubNationalDivisionService.getSubNationalDivisions().subscribe({
      next: (SubNationalDivisions: SubNationalDivision[]) => {
        if (!SubNationalDivisions) {
          throw this.ToastService.addErrorToast(
            'Error',
            'Error retrieving SubNationalDivisions.',
            { returnError: true }
          );
        }

        this.SubNationalDivisions = SubNationalDivisions;
      }
    });

    this.contactForm.valueChanges.subscribe({
      next: (changes) => {
        this.Contact = changes;
      }
    });
  }

  validateContactForm(): boolean {
    if (this.contactForm) {
      const errors = this.UtilityService.generateValidationErrorsObject(
        this.contactForm
      );

      if (errors) {
        throw this.ToastService.addErrorToast(
          'Error',
          'There are missing required fields.',
          { returnError: true }
        );
      }

      return true;
    }

    return false;
  }

  saveContact() {
    if (!this.Contact || !this.validateContactForm()) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Contact is not valid to save.',
        { returnError: true }
      );
    }

    const getContactsQuery = {};

    if (this.SiteId) {
      Object.assign(this.Contact, { SiteId: this.SiteId });
      Object.assign(getContactsQuery, { SiteId: this.SiteId });
    }

    const observableArr = [];

    if (this.Contact.isPrimary) {
      observableArr.push(
        defer(() => {
          return this.ContactService.getContacts(getContactsQuery).pipe(
            concatMap((Contacts: Contact[]) => {
              if (Contacts && Contacts.length > 0) {
                const primaryContact = Contacts.find(
                  (contact) => contact.isPrimary
                );

                if (primaryContact) {
                  this.previousPrimaryContact = primaryContact;
                }
              }

              return of(true);
            })
          );
        })
      );
    }

    observableArr.push(
      defer(() => {
        if (this.previousPrimaryContact) {
          const modalRef = this.ModalService.open(ConfirmModalComponent, {
            centered: true
          });
          modalRef.componentInstance.displayText =
            'A primary contact already exists. Do you wish to replace existing primary contact?';

          return modalRef.closed.pipe(
            concatMap((confirm) => {
              if (confirm && this.previousPrimaryContact) {
                Object.assign(this.previousPrimaryContact, {
                  isPrimary: false
                });
                return this.ContactService.saveContact(
                  this.previousPrimaryContact
                ).pipe(
                  concatMap((Contact: Contact) => {
                    if (Contact) {
                      return of(true);
                    }

                    throw this.ToastService.addErrorToast(
                      'Error',
                      'There was an error updating previous primary contact.',
                      { returnError: true }
                    );
                  })
                );
              }

              this.modal.close(false);
              return of(false);
            })
          );
        }

        return of(true);
      })
    );

    observableArr.push(
      defer(() => {
        if (this.Contact) {
          return this.ContactService.saveContact(this.Contact).pipe(
            concatMap((Contact: Contact) => {
              if (Contact) {
                this.ToastService.addSuccessToast(
                  'Success',
                  'Successfully saved contact.'
                );
                this.modal.close(true);
              }

              return of(true);
            })
          );
        }

        return of(false);
      })
    );

    concat(...observableArr)
      .pipe(takeWhile((result) => result !== false))
      .subscribe();
  }

  cancel() {
    this.modal.close(false);
  }

  ngOnInit(): void {
    if (!this.isSiteContact) {
      throw this.ToastService.addErrorToast(
        'Error',
        'No contact type selected.',
        { returnError: true }
      );
    }

    if (!this.useMailingAddress && !this.useStreetAddress) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Must assign useStreetAddress or useMailingAddress.',
        { returnError: true }
      );
    }

    if (this.ContactId && this.SiteId) {
      const contactsQuery = {
        id: this.ContactId,
        SiteId: this.SiteId
      };

      if (this.useStreetAddress) {
        Object.assign(contactsQuery, { StreetAddress: JSON.stringify({}) });
      }

      if (this.useMailingAddress) {
        Object.assign(contactsQuery, { MailingAddress: JSON.stringify({}) });
      }

      this.ContactService.getContacts(contactsQuery).subscribe({
        next: (Contacts: Contact[]) => {
          if (!Contacts || Contacts.length > 1) {
            throw this.ToastService.addErrorToast(
              'Error',
              'Should have only fetched one contact record.',
              { returnError: true }
            );
          }

          this.Contact = Contacts[0];
          this.contactForm?.patchValue(this.Contact);
        }
      });
    }

    if (this.useMailingAddress && this.contactForm) {
      this.contactForm.removeControl('StreetAddress');
      this.contactForm.get('MailingAddress.isMailing')?.patchValue(true);
    }

    if (this.useStreetAddress && this.contactForm) {
      this.contactForm.removeControl('MailingAddress');
      this.contactForm.get('StreetAddress.isStreet')?.patchValue(true);
    }
  }
}
